import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bike, BatteryCharging, Sparkles, UtensilsCrossed, Info, X } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import { useStore } from '../store/useStore'
import { api } from '../services/api'
import type { Announcement, AnnouncementCategory } from '../services/api/announcements'
import { excludedIds, markDismissed, markShown } from '../lib/announcementFrequency'

/**
 * #1384 旅先で役に立つお知らせのポップアップ。
 *
 * 「修善寺に着いたらレンタサイクルの案内が出て、タップすると詳細に飛ぶ」を実現する。
 *
 * **なぜ push ではなくアプリ内ポップアップが主なのか**:
 * ブラウザはアプリを閉じている間、位置を取得できない（Geofencing API は事実上使えず、
 * watchPosition もページが開いている間だけ）。「ある地域に入ったら自動で通知」は
 * Web では実現できないため、**開いたときに現在地を見て出す**形にしている。
 * push は #1384 Phase 2 で補助として足す。
 *
 * 位置が取れないときは「場所を問わない案内」だけが出る（位置連動の案内を位置不明の
 * まま出すと、無関係な場所で「ここにあります」と言うことになり信用を落とす）。
 */

const ICONS: Record<AnnouncementCategory, typeof Info> = {
  transport: Bike,
  charging: BatteryCharging,
  food: UtensilsCrossed,
  experience: Sparkles,
  other: Info,
}

export default function AnnouncementPopup({
  lat,
  lng,
}: {
  lat?: number | null
  lng?: number | null
}) {
  const navigate = useNavigate()
  const { t, locale } = useI18n()
  const [item, setItem] = useState<Announcement | null>(null)
  const residencePrefecture = useStore((s) => s.user?.residencePrefecture ?? '')
  const residenceCity = useStore((s) => s.user?.residenceCity ?? '')

  // #1384 Phase 2 位置が取れているなら「最後に判明した位置」として購読へ記録する。
  // これが push の宛先判定に使われる (6時間以内ならまだ近くに居るとみなす)。
  // 購読していない人・未ログインの人は API 側で 0 件更新になるだけ。
  useEffect(() => {
    if (lat == null || lng == null) return
    api.reportPushLocation(lat, lng).catch(() => {})
  }, [lat, lng])

  // 一度出したら、この画面にいる間は取り直さない。位置や居住地が後から分かって条件が変わっても、
  // 読んでいる最中のお知らせを別のものに差し替えない (表示回数も二重に数えない)
  const shownRef = useRef(false)

  useEffect(() => {
    if (shownRef.current) return
    let cancelled = false
    api.fetchAnnouncements({
      lat, lng, locale, excludeIds: excludedIds(), limit: 1,
      // #2004 住んでいる町あての案内を受け取るため、自分の居住地を添える (未回答なら添えない)
      residence: residencePrefecture && residenceCity
        ? { prefecture: residencePrefecture, city: residenceCity } : null,
    })
      .then((rows) => {
        if (cancelled || rows.length === 0) return
        const picked = rows[0]
        shownRef.current = true
        setItem(picked)
        markShown(picked.id)
        // 配信時ではなく**表示時**に数える。頻度制御で出さなかった分を表示回数に
        // 含めると、「役に立っているか」の判断を誤る。
        api.recordAnnouncementImpression(picked.id).catch(() => {})
      })
      .catch((e: unknown) => console.warn('[Announcement] fetch failed:', e))
    return () => { cancelled = true }
  }, [lat, lng, locale, residencePrefecture, residenceCity])

  if (!item) return null

  const Icon = ICONS[item.category] || Info

  const close = () => {
    markDismissed(item.id)     // 閉じた = 興味なし。以後は出さない
    setItem(null)
  }

  const open = () => {
    api.recordAnnouncementClick(item.id).catch(() => {})
    markDismissed(item.id)     // 一度開いたら役目を終えている
    const url = item.linkUrl
    setItem(null)
    if (!url) return
    if (/^https?:\/\//.test(url)) window.open(url, '_blank', 'noopener,noreferrer')
    else navigate(url)
  }

  // fixed ではなく sticky。
  //
  // fixed だと本文の上に浮くだけで**レイアウト上の場所を持たない**ため、この画面の
  // 末尾（近くの店舗）がカードの下に入って隠れていた。sticky なら画面下に貼り付いた
  // まま、本文の末尾に自分の場所を確保するので、隠れたままになる部分が生まれない。
  //
  // 横幅は本文のカードに揃える。以前は px-4 を**画面端**に当てたうえで
  // max-w-[430px] にしていたため、画面が 430px より広いと padding が効かず、
  // mobile-container と同じ幅（＝本文のカードより左右 16px ずつ広い）になっていた。
  return (
    <div className="sticky bottom-0 z-40 pb-24 animate-slide-up">
      <div className="mx-auto w-full max-w-[430px] px-4">
        <div className="overflow-hidden rounded-2xl bg-white shadow-card">
            {item.imageUrl && (
              <img src={item.imageUrl} alt="" className="h-20 w-full object-cover" />
            )}
            <div className="flex items-start gap-3 p-4">
              <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-matcha/15">
                <Icon size={18} className="text-matcha" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <p className="min-w-0 flex-1 truncate font-display text-sm font-bold text-text-primary">
                    {item.title}
                  </p>
                  {/* 掲載料を受け取っている枠は PR 表記が必須 (景品表示法のステマ規制)。
                    * ここを省くと違反になるので、条件分岐を消さないこと。 */}
                  {item.sponsored && (
                    <span className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 font-body text-[9px] font-bold text-text-tertiary">
                      {t('announcement.sponsored')}
                    </span>
                  )}
                </div>
                {item.body && (
                  <p className="mt-0.5 font-body text-xs leading-snug text-text-secondary">
                    {item.body}
                  </p>
                )}
                {item.linkUrl && (
                  <button
                    onClick={open}
                    className="mt-2 h-9 w-full rounded-xl bg-navy font-body text-xs font-semibold text-white tap-effect"
                  >
                    {t('announcement.open')}
                  </button>
                )}
              </div>
              <button
                onClick={close}
                className="shrink-0 rounded-full p-1 tap-effect"
                aria-label={t('announcement.close')}
              >
                <X size={16} className="text-text-tertiary" />
              </button>
            </div>
          </div>
      </div>
    </div>
  )
}
